import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faCircle,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Using the same styling module as the main Chatbot component
import styles from "../../../styles/patient-page-modules/Chatbot.module.css";
import Button from "../../button-v1";
import toast from "react-hot-toast";
import axios from "axios";
import StreamingText from "../../streamingText";
import { Checkbox } from "../../../components/ui/checkbox";
import { List } from "../../../components/ui/list";
import { Dropdown } from "../../../components/ui/dropdown";
import {
  getPatientDocuments,
  uploadChatbotContext,
  uploadPatientDocuments,
} from "../../../services/patient/documents";
import { parseDateSafe } from "../../../utils/dateFormatters";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import UploadFiles from "../../shared-modules/upload-files/index";

const ContextSelection = ({
  setIsChatStarted,
  setShowContextView,
  setSession,
}) => {
  const filterOptions = [
    { value: "1", label: "1 Month" },
    { value: "2", label: "2 Month" },
    { value: "3", label: "3 Month" },
    { value: "all", label: "All" },
  ];

  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState("1");
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [openSections, setOpenSections] = useState({});
  const patientId = searchParams.get("patient_id");

  const uploadContextMutation = useMutation(
    () => {
      const payload = selectedDocs.map((_doc) => {
        const { document_type, document_url } = _doc;
        return { document_type, document_url };
      });

      return uploadChatbotContext({
        patient_id: patientId,
        Payload: payload,
      });
    },
    {
      onSuccess: (data) => {

        if (data?.session_id) {
          setSession(data.session_id);
        }

        setShowContextView(false);
        setIsChatStarted(true);
      },
      onError: (error) => {
        toast.error("Failed to set context.");
        console.error(error);
      }
    }
  );

  const patientDocumentsQuery = useQuery(
    ["patient-documents", filter],
    () => {
      if (filter === "all") {
        return getPatientDocuments();
      } else {
        return getPatientDocuments(null, filter);
      }
    },
    {
      enabled: !!filter,
      select: (data) => {
        const allowedTypes = [
          "CONSENT",
          "DOCTOR",
          "CAREPLAN",
          "ADDITIONAL",
          "LAB",
          "IMAGING",
        ];

        const filtered = Array.isArray(data)
          ? data.filter((_doc) => allowedTypes.includes(_doc?.document_type))
          : [];

        const grouped = filtered.reduce((acc, _doc) => {
          const d = parseDateSafe(_doc.uploaded_at) || new Date();
          const monthKey = d.toLocaleString("default", {
            month: "long",
            year: "numeric",
          });

          if (!acc[monthKey]) acc[monthKey] = [];
          acc[monthKey].push(_doc);

          return acc;
        }, {});

        const typePriority = [
          "CONSENT",
          "DOCTOR",
          "CAREPLAN",
          "LAB",
          "IMAGING",
        ];

        const getPriorityIndex = (type) => {
          const idx = typePriority.indexOf(type ?? "");
          return idx === -1 ? typePriority.length : idx;
        };

        Object.keys(grouped).forEach((monthKey) => {
          grouped[monthKey].sort((a, b) => {
            const aIdx = getPriorityIndex(a?.document_type);
            const bIdx = getPriorityIndex(b?.document_type);
            if (aIdx !== bIdx) return aIdx - bIdx;
            const aDate = (parseDateSafe(a?.uploaded_at) || new Date(0)).getTime();
            const bDate = (parseDateSafe(b?.uploaded_at) || new Date(0)).getTime();
            return bDate - aDate;
          });
        });

        return Object.entries(grouped).map(([month, docs]) => ({
          month,
          docs,
        }));
      },
    }
  );

  const DocList = ({ docs, selectedDocs, onDocToggle, onSelectAll }) => {
    return (
      <section className={styles.doc_list}>
        {(() => {
          const allSelected =
            docs.length > 0 &&
            docs.every((_d) => selectedDocs.some((s) => s.id === _d.id));
          return (
            <div
              key={"select-all"}
              className={`${styles.doc_item} ${
                allSelected ? styles.selected : ""
              }`}
              onClick={() => onSelectAll(docs)}
            >
              <section className={styles.doc_selection}>
                <Checkbox
                  className={styles.action_checkbox}
                  checked={allSelected}
                  onCheckedChange={() => onSelectAll(docs)}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className={styles.doc_name}>Select all</span>
              </section>
            </div>
          );
        })()}
        {docs.map((_doc) => (
          <div
            key={_doc.uploaded_at}
            className={`${styles.doc_item} ${
              selectedDocs.some((doc) => doc.id === _doc.id)
                ? styles.selected
                : ""
            }`}
            onClick={() => onDocToggle(_doc.id)}
          >
            <section className={styles.doc_selection}>
              <Checkbox
                className={styles.action_checkbox}
                checked={selectedDocs.some((doc) => doc.id === _doc.id)}
                onCheckedChange={() => onDocToggle(_doc.id)}
                onClick={(e) => e.stopPropagation()}
              />
              <span className={styles.doc_name}>{_doc.document_name} </span>
              <em>({_doc.document_type.toLowerCase()})</em>
            </section>
            <span className={styles.doc_date}>
              {parseDateSafe(_doc.uploaded_at)?.toLocaleDateString() || ""}
            </span>
          </div>
        ))}
      </section>
    );
  };

  const handleDocToggle = (docId) => {
    setSelectedDocs((prev) => {
      const existingDoc = prev.find((doc) => doc.id === docId);
      if (existingDoc) {
        return prev.filter((doc) => doc.id !== docId);
      } else {
        const allDocs =
          patientDocumentsQuery.data?.flatMap(
            (monthGroup) => monthGroup.docs
          ) || [];
        const docToAdd = allDocs.find((doc) => doc.id === docId);
        return docToAdd ? [...prev, docToAdd] : prev;
      }
    });
  };

  const handleSelectAllForDocs = (docs) => {
    setSelectedDocs((prev) => {
      const allSelected = docs.every((_d) => prev.some((s) => s.id === _d.id));
      if (allSelected) {
        const idsToRemove = new Set(docs.map((d) => d.id));
        return prev.filter((s) => !idsToRemove.has(s.id));
      } else {
        const existingIds = new Set(prev.map((s) => s.id));
        const toAdd = docs.filter((d) => !existingIds.has(d.id));
        return [...prev, ...toAdd];
      }
    });
  };

  const handleSelectAllAcrossMonths = () => {
    const allDocs =
      patientDocumentsQuery.data?.flatMap((group) => group.docs) || [];

    setSelectedDocs((prev) => {
      if (allDocs.length === 0) return prev;
      const allSelected = allDocs.every((d) => prev.some((s) => s.id === d.id));
      if (allSelected) {
        const idsToRemove = new Set(allDocs.map((d) => d.id));
        return prev.filter((s) => !idsToRemove.has(s.id));
      } else {
        const existingIds = new Set(prev.map((s) => s.id));
        const toAdd = allDocs.filter((d) => !existingIds.has(d.id));
        return [...prev, ...toAdd];
      }
    });
  };

  const handleSectionToggle = (month) => {
    setOpenSections((prev) => ({
      ...prev,
      [month]: !prev[month],
    }));
  };

  return (
    <section className={styles.container}>
      <section className={styles.context_header_section}>
        <h4>Select Documents ({selectedDocs.length})</h4>
        <section className={styles.chatbot_context_actions}>
          <Button
            variant="ghost"
            size="small"
            onClick={handleSelectAllAcrossMonths}
            style={{ marginLeft: 8 }}
          >
            Select all
          </Button>
          <section className={styles.context_header_actions}>
            <Dropdown
              options={filterOptions}
              value={filter}
              onChange={(value) => setFilter(value)}
              trigger={
                <Button variant="ghost" size="small">
                  <img
                    src="/icons/calendar-clock.svg"
                    alt="filter"
                    style={{ width: "14px", height: "14px" }}
                  />
                  {filterOptions.find((type) => type.value === filter)?.label ||
                    "Filter"}
                  <img
                    src="/icons/arrow-down.svg"
                    alt="arrow"
                    style={{ width: "12px", height: "12px" }}
                  />
                </Button>
              }
            />
          </section>
        </section>
      </section>
      <section className={styles.context_list_container}>
        {patientDocumentsQuery.data?.map((_doc) => (
          <List
            key={_doc.month}
            title={_doc.month}
            content={
              <DocList
                docs={_doc.docs}
                selectedDocs={selectedDocs}
                onDocToggle={handleDocToggle}
                onSelectAll={handleSelectAllForDocs}
              />
            }
            isOpen={openSections[_doc.month] !== false}
            onClose={() => handleSectionToggle(_doc.month)}
            background="var(--light-blue)"
          />
        ))}
      </section>
      <div className={styles.action_buttons}>
        <Button
          variant="ghost"
          disabled={uploadContextMutation.isLoading}
          onClick={() => {
            setShowContextView(false);
            setIsChatStarted(true);
          }}
        >
          Skip
        </Button>
        <Button
          variant="ghost"
          onClick={() => setSelectedDocs([])}
          disabled={
            selectedDocs.length === 0 || uploadContextMutation.isLoading
          }
        >
          Clear Selection
        </Button>
        <Button
          variant="primary"
          loading={uploadContextMutation.isLoading}
          onClick={() => uploadContextMutation.mutate()}
          disabled={
            selectedDocs.length === 0 || uploadContextMutation.isLoading
          }
        >
          Select
        </Button>
      </div>
    </section>
  );
};

const Chatbot = ({ chatProps }) => {
  const {
    chatBotType,
    isChatStarted,
    setIsChatStarted,
    messages,
    message,
    setMessage,
    chatContainerRef,
    handleSubmit,
    showGeneralContextView,
    setShowGeneralContextView,
    setGeneralSessionId,
    showUploadModal,
    setShowUploadModal,
  } = chatProps;

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const queryClient = useQueryClient();

  // Document upload mutation
  const patientDocumentsMutation = useMutation(
    (formData) => uploadPatientDocuments(formData),
    {
      onSuccess: () => {
        toast.success("Documents uploaded successfully!");
        setShowUploadModal(false);
        setUploadedFiles([]);
        queryClient.invalidateQueries(["patient-documents"]);
      },
      onError: (error) => {
        toast.error("Failed to upload documents. Please try again.");
        console.error("Upload error:", error);
      },
    }
  );

  const handleDocumentsUpload = () => {
    if (uploadedFiles.length <= 0) {
      toast.error("Please select at least one document to upload.");
      return;
    }

    const allowedExtensions = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
    const invalidFiles = uploadedFiles.filter((_doc) => {
      const fileName = _doc.file.name.toLowerCase();
      const extension = fileName.split(".").pop();
      return !allowedExtensions.includes(extension);
    });

    if (invalidFiles.length > 0) {
      toast.error(
        `Invalid file type detected. Allowed types: .pdf, .doc, .docx, .jpg, .jpeg, .png`
      );
      return;
    }

    const formData = new FormData();
    uploadedFiles.forEach((_doc, i) => {
      formData.append("documents", _doc.file);
      formData.append(`document_type_${i}`, _doc.type);
    });

    patientDocumentsMutation.mutate(formData);
  };

  // scrolling related functions
  useEffect(() => {
    requestAnimationFrame(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTo({
          top: chatContainerRef.current.scrollHeight,
        });
      }
    });
  }, [chatBotType]);

  const checkScrollPosition = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        chatContainerRef.current;
      setShowScrollButton(scrollTop + clientHeight < scrollHeight - 20);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollPosition);
      return () => container.removeEventListener("scroll", checkScrollPosition);
    }
  }, []);

  return (
    <>
      {!isChatStarted && !showGeneralContextView && (
        <div
          className={styles.questions_box}
          style={{ backgroundColor: "#DFEBF6" }}
        >
          <div className={styles.welcome_container}>
            <img
              src="/assets/images/chat.png"
              alt="toca co_pilot"
              className={styles.chatbot_image_small}
            />
            <h3 style={{ color: "#10164d" }}>TOCA General CoPilot</h3>
            <p className={styles.welcome_subtitle}>
              Your general-purpose AI assistant for patient care
            </p>

            <div className={styles.instructions_container}>
              <div className={styles.instruction_step}>
                <span className={styles.step_badge}>1</span>
                <div className={styles.step_content}>
                  <div className={styles.step_title}>Select Context</div>
                  <div className={styles.step_desc}>
                    Choose patient documents and records to give CoPilot relevant context
                  </div>
                </div>
              </div>
              <div className={styles.instruction_step}>
                <span className={styles.step_badge}>2</span>
                <div className={styles.step_content}>
                  <div className={styles.step_title}>Ask Questions</div>
                  <div className={styles.step_desc}>
                    Ask about patient history, medications, lab results, or care guidelines
                  </div>
                </div>
              </div>
              <div className={styles.instruction_step}>
                <span className={styles.step_badge}>3</span>
                <div className={styles.step_content}>
                  <div className={styles.step_title}>Get AI-Powered Answers</div>
                  <div className={styles.step_desc}>
                    Receive informed responses grounded in the patient's data and clinical context
                  </div>
                </div>
              </div>
            </div>

            <Button
              variant={"primary"}
              size="medium"
              onClick={() => setShowGeneralContextView(true)}
              className={styles.start_button}
            >
              Start Chat
            </Button>
          </div>
        </div>
      )}

      {showGeneralContextView && !isChatStarted && (
        <ContextSelection
          setIsChatStarted={setIsChatStarted}
          setShowContextView={setShowGeneralContextView}
          setSession={setGeneralSessionId}
        />
      )}

      {isChatStarted && (
        <div
          className={styles.questions_box}
          style={{ backgroundColor: "#DFEBF6", position: "relative" }}
        >
          <div
            ref={chatContainerRef}
            className={styles.chat_container}
            onScroll={checkScrollPosition}
          >
            {(() => {
              const lastBotIndex = [...messages]
                .reverse()
                .findIndex((msg) => msg.type === "bot");
              const actualLastBotIndex =
                lastBotIndex === -1 ? -1 : messages.length - 1 - lastBotIndex;

              return messages.map((msg, index) => (
                <div
                  key={index}
                  className={`${styles.chat_message} ${
                    msg.type === "bot"
                      ? `${styles.bot_message} ${styles.ccm_bot_msg}`
                      : `${styles.user_message} ${styles.ccm_bot_msg}`
                  }`}
                >
                  {msg.loading ? (
                    <FontAwesomeIcon
                      icon={faCircle}
                      className={styles.blinking_loader}
                    />
                  ) : msg.streaming ? (
                    <StreamingText text={msg.text?.trim()} isRealStream={true} />
                  ) : msg.streamed ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text?.trim()}</ReactMarkdown>
                  ) : msg.type === "bot" && index === actualLastBotIndex ? (
                    <StreamingText
                      text={msg.text?.trim()}
                      enableMarkdown={true}
                    />
                  ) : (
                    msg.text?.trim()
                  )}
                </div>
              ));
            })()}
          </div>
          {showScrollButton && (
            <button
              onClick={scrollToBottom}
              style={{
                position: "absolute",
                bottom: "10px",
                right: "50",
                width: "35px",
                height: "35px",
                borderRadius: "50%",
                backgroundColor: "rgb(145 145 145)",
                color: "white",
                border: "1px solid white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                zIndex: 1000,
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "rgb(109 108 108)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "rgb(145 145 145)")
              }
            >
              <FontAwesomeIcon icon={faArrowDown} />
            </button>
          )}
        </div>
      )}

      {isChatStarted && (
        <form className={styles.message_form}>
          <textarea
            placeholder="Type prompt here..."
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height =
                Math.min(e.target.scrollHeight, 75) + "px";
            }}
            onKeyDown={(e) => {
              if (
                messages.length > 0 &&
                messages[messages.length - 1].loading
              ) {
                return;
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
                e.target.style.height = "auto";
              }
            }}
            className={styles.message_input}
            rows={1}
          />

          <Button
            disabled={
              messages.length > 0 && messages[messages.length - 1].loading
            }
            type="button"
            size="small"
            onClick={(e) => handleSubmit(e)}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </Button>
        </form>
      )}

      {/* Documents upload Modal */}
      <Dialog open={showUploadModal} setOpen={() => setShowUploadModal(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Documents</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <section className={styles.upload_container}>
              <UploadFiles
                accept=".pdf, .doc, .docx, .jpg, .jpeg, .png"
                showFileList={true}
                placeholder="Drop your files here"
                subText="Or click to browse"
                buttonText="Browse"
                filesHandler={(files) => setUploadedFiles(files)}
                onSuccess={patientDocumentsMutation.isSuccess}
                options={[
                  { label: "Consent", value: "consent" },
                  { label: "Additional", value: "additional" },
                  { label: "Lab Reports", value: "lab" },
                  { label: "Imaging", value: "imaging" },
                ]}
              />
            </section>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowUploadModal(false)}>
              Cancel
            </Button>
            <Button
              disabled={uploadedFiles.length <= 0}
              loading={patientDocumentsMutation.isLoading}
              onClick={() => handleDocumentsUpload()}
            >
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Chatbot;